import { FormEvent, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { LoadingComp } from "../../components/loading/loading.comp";
import { useData } from "../../libs/data/app-data.hook";
import { useRegistration } from "../../libs/registration/registration.hook";
import { RegistrationContainer } from "./styles/registration-container.style";
import { SingleInput } from "../../components/form/styles/single-input";
import { AppForm } from "../../components/form/styles/app-form";
import { StudentInfoComp } from "../../components/student-info/student-info.comp";
import { TermsV2Comp } from "../../components/terms/terms-v2.comp";
import { CtaButton } from "../../components/form/styles/cta-button";
import { useReferral } from "../../libs/referral/useReferral";

const dayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

export const RegisterView = () => {
  const { referral } = useReferral();
  const { eventId } = useParams();
  const { loading, activities } = useData();
  const {
    processing,
    registrationInfo,
    setRegistrationInfo,
    sendRegistration,
  } = useRegistration();
  const currentEvent = useMemo(
    () => (activities?.all ? activities.all[`${eventId}`] : {}),
    [eventId, activities]
  );
  const isSchedule = currentEvent && currentEvent.tags?.includes("schedule");
  const isPencil = currentEvent && currentEvent.tags?.includes("pencil");
  const isAcrylic = currentEvent && currentEvent.tags?.includes("acrylic");

  const isEmail =
    registrationInfo.email?.includes(".") &&
    registrationInfo.email?.includes("@");

  const isMonthly = currentEvent.type === "schedule";

  const canProceed = useMemo(
    () =>
      registrationInfo.students.participant?.firstName &&
      registrationInfo.students.participant?.lastName &&
      registrationInfo.students.participant?.dateOfBirth &&
      registrationInfo.firstName &&
      registrationInfo.lastName &&
      registrationInfo.phone &&
      registrationInfo.email &&
      registrationInfo.liabilitySigned &&
      registrationInfo.paymentSigned &&
      registrationInfo.returnsSigned &&
      registrationInfo.photoReleaseSigned &&
      registrationInfo.medicalSigned &&
      registrationInfo.codeSigned &&
      isEmail &&
      (!isMonthly || (isMonthly && registrationInfo.cancellationSigned)),
    [registrationInfo]
  );

  useEffect(() => {
    if (referral) {
      setRegistrationInfo({
        referral,
      });
    }
  }, [referral]);

  useEffect(() => {
    setRegistrationInfo({
      event: eventId,
    });
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (canProceed) {
      try {
        await sendRegistration();

        const withFood = registrationInfo.foodOption === "yes";
        const extendedHours = registrationInfo.extendedHours === "yes";

        if (withFood && extendedHours && currentEvent.paymentFull) {
          window.location.href = currentEvent.paymentFull;
        } else if ((withFood || extendedHours) && currentEvent.paymentExtra) {
          window.location.href = currentEvent.paymentExtra;
        } else {
          window.location.href = currentEvent.payment;
        }
      } catch (err) {
        alert("Registration failed. Please try again.");
      }
    }
  };

  const onAddFood = (event: FormEvent) => {
    setRegistrationInfo({
      foodOption: !registrationInfo.foodOption ? "yes" : "",
    });
  };

  const onAddExtendedHours = (event: FormEvent) => {
    setRegistrationInfo({
      extendedHours: !registrationInfo.extendedHours ? "yes" : "",
    });
  };

  return (
    <RegistrationContainer>
      <h1>
        Registration for{" "}
        <strong>{currentEvent?.name ? `${currentEvent.name}` : ""}</strong>
        <br />
        {currentEvent && isSchedule && (
          <>
            <small>
              {dayNames[currentEvent.day]} | {currentEvent.startTime} -{" "}
              {currentEvent.endTime} | {currentEvent.description}
            </small>
            <br />
            <br />
            {currentEvent.price && <small>Price: {currentEvent.price}</small>}
          </>
        )}
      </h1>

      <AppForm onSubmit={onSubmit}>
        {isAcrylic && (
          <div className="text-left">
            <section>
              <h2>
                🎉 Thank you for your interest in Registering with
                Colourinspired Academy!
              </h2>
              <p>
                We’re excited to welcome your child to our creative community!
              </p>
            </section>

            <section>
              <h2>🎨 Acrylic Painting Class – Colour Inspired Academy</h2>
              <p>Beginner to intermediate</p>
              <p>Instructor-led, hands-on class with step-by-step guidance</p>
              <p>
                Welcome to the colourful world of acrylic painting! In this fun
                and expressive class, young artists will learn how to work with
                one of the most versatile and vibrant painting mediums — acrylic
                paint.
              </p>
              <p>
                Children will explore basic colour theory, blending techniques,
                brush handling, and composition skills while creating their own
                original masterpieces on canvas or heavy paper.
              </p>
              <p>
                Each session will be guided step-by-step by our instructor, with
                plenty of room for personal expression and creativity. We’ll
                practice layering, experimenting with texture, and even learning
                how to fix mistakes (a true artist’s secret!).
              </p>
              <p>
                From bold landscapes to whimsical animals and abstract art, this
                class is all about confidence, creativity, and colour.
              </p>
              🧠 What Kids Will Learn:
              <ul>
                <li>
                  ✅ How to mix colours using primary and secondary palettes
                </li>
                <li>
                  ✅ Basic brush techniques: flat brush, round brush, detail
                  work
                </li>
                <li>✅ Painting from background to foreground (layering)</li>
                <li>✅ Light and shadow in colour</li>
                <li>✅ Creative problem solving and artistic expression</li>
              </ul>
              <p>
                <strong>
                  Every child will finish the class with a completed painting
                  they are proud of — ready to take home or display in our
                  studio’s mini art show!
                </strong>
              </p>
            </section>

            <section>
              <h2>📞 Questions?</h2>
              <p>If you have any questions, feel free to contact us:</p>
              <ul>
                <li>
                  <strong>Anna (Administrator):</strong> 647-927-5083
                </li>
                <li>
                  <strong>Jane (Director):</strong> 647-236-6647
                </li>
              </ul>
              <p>We’re happy to help!</p>
            </section>
          </div>
        )}

        {isPencil && (
          <div className="text-left">
            <section>
              <h2>
                🎉 Thank you for your interest in Registering with
                Colourinspired Academy!
              </h2>
              <p>
                We’re excited to welcome your child to our creative community!
              </p>
            </section>

            <section>
              <h2>🎨 Intro to Pencil Drawing – Colour Inspired Academy</h2>
              <p>
                Give your child the gift of creativity and confidence through
                our engaging 12-class pencil drawing program! This hands-on
                course is perfect for young artists who want to build real
                drawing skills while exploring their imagination.
              </p>
              Children will learn:
              <ul>
                <li>✅ How to use professional drawing tools</li>
                <li>
                  ✅ Techniques for shading, texture, and realistic sketching
                </li>
                <li>
                  ✅ The foundations of perspective, portrait drawing, and
                  composition
                </li>
                <li>
                  ✅ Fun art history tie-ins and outdoor sketching activities
                </li>
              </ul>
              <p>
                From simple shapes to stunning still lifes and portraits,
                students will progress through a carefully designed curriculum
                that balances technical skills with creative freedom. By the end
                of the program, your child will complete a final project and
                receive a certificate of completion.
              </p>
              <p>
                <strong>
                  Let their creativity shine — one pencil stroke at a time!
                </strong>
              </p>
            </section>

            <section>
              <h2>📞 Questions?</h2>
              <p>If you have any questions, feel free to contact us:</p>
              <ul>
                <li>
                  <strong>Anna (Administrator):</strong> 647-927-5083
                </li>
                <li>
                  <strong>Jane (Director):</strong> 647-236-6647
                </li>
              </ul>
              <p>We’re happy to help!</p>
            </section>
          </div>
        )}

        <h2>Child/Participant Information</h2>
        <StudentInfoComp
          id="participant"
          showSelection={false}
          showOptional={true}
        />

        {(currentEvent?.showFoodOption ||
          currentEvent?.showExtendedHoursOption) && <h2>Camp Options</h2>}
        <div className="text-left">
          {currentEvent?.showFoodOption && (
            <p>
              <input
                type="checkbox"
                checked={registrationInfo.foodOption === "yes"}
                onChange={onAddFood}
              />{" "}
              Add food option
            </p>
          )}
          {currentEvent?.showExtendedHoursOption && (
            <p>
              <input
                type="checkbox"
                checked={registrationInfo.extendedHours === "yes"}
                onChange={onAddExtendedHours}
              />{" "}
              Add extended hours option
            </p>
          )}
        </div>

        <h2>Parent / Guardian Information</h2>
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ firstName: event.target.value })
          }
          value={registrationInfo.firstName}
          placeholder="First name"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ lastName: event.target.value })
          }
          value={registrationInfo.lastName}
          placeholder="Last name"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ email: event.target.value })
          }
          value={registrationInfo.email}
          placeholder="Email"
        />
        <SingleInput
          onChange={(event: any) =>
            setRegistrationInfo({ phone: event.target.value })
          }
          value={registrationInfo.phone}
          placeholder="Phone"
        />

        <h2>Terms and Conditions</h2>
        <TermsV2Comp isMonthly={isMonthly} />

        <CtaButton disabled={!canProceed}>Register</CtaButton>
        <ul className="error-summary">
          {!registrationInfo.students.participant?.firstName ? (
            <li>Participant First Name is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.students.participant?.lastName ? (
            <li>Participant Last Name is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.students.participant?.dateOfBirth ? (
            <li>Participant Date of Birth is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.firstName ? <li>First Name is required</li> : ""}
          {!registrationInfo.lastName ? <li>Last Name is required</li> : ""}
          {!registrationInfo.phone ? <li>Phone is required</li> : ""}
          {!isEmail ? <li>Email is required</li> : ""}
          {!registrationInfo.liabilitySigned ? (
            <li>Liability Waiver is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.paymentSigned ? (
            <li>Payment agreement is required</li>
          ) : (
            ""
          )}
          {isMonthly && !registrationInfo.cancellationSigned ? (
            <li>Cancellation fee agreement is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.returnsSigned ? (
            <li>Return Policy is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.photoReleaseSigned ? (
            <li>Photo Release is required</li>
          ) : (
            ""
          )}
          {!registrationInfo.medicalSigned ? (
            <li>Allergies or Medical Conditions are required</li>
          ) : (
            ""
          )}
          {!registrationInfo.codeSigned ? (
            <li>Code of Conduct is required</li>
          ) : (
            ""
          )}
        </ul>
        <br />
        <br />
      </AppForm>
      {(loading || processing) && <LoadingComp />}
    </RegistrationContainer>
  );
};
